import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { permissionGuard } from '@adornis/user-forms/permission-guard.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { CampusArea } from '@chancenwerk/campus-learning-content/entities/CampusSection.js';
import '@chancenwerk/components/chancen-component-context-menu.js';
import { availableCampusSubjects } from '@chancenwerk/db/enums/CampusSubject.js';
import { ChancenPermission } from '@chancenwerk/db/permissions.js';
import '@chancenwerk/design/components/cr-button.js';
import '@chancenwerk/design/components/cr-checkbox.js';
import '@chancenwerk/design/components/cr-image.js';
import '@chancenwerk/design/components/cr-tabs.js';
import '@chancenwerk/design/components/fonts.js';
import { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { guard } from 'lit/directives/guard.js';
import '../components/campus-component-eventbox.js';
import '../components/campus-component-eventlist.js';
import '../components/campus-component-section-lesson-list.js';
import { CampusDialogEditSection } from '../components/dialogs/campus-dialog-edit-section.js';
import { CampusRoutes } from '../routes.js';

/**
 * @customElement campus-page-editorial
 */
@customElement('campus-page-editorial')
export class CampusPageEditorial extends ChemistryLitElement {
  @state() private readonly _currentUser = new RXController(this, currentUser<ChancenwerkUser>());

  // Filtering properties
  @state() private readonly _userCheckedSubjects = new RXController<string[]>(this, []);
  @state() private readonly _userCheckedGrades = new RXController<number[]>(this, []);
  @state() private readonly _userSearchText = new RXController<string>(this, '');
  @state() private readonly _userHiddenFromStudents = new RXController<Maybe<boolean>>(this, null);

  @state() private _updateList = 0;

  @state() size = new WindowSizeController(this);

  override render() {
    return permissionGuard(ChancenPermission.campus.EDIT_CONTENT, () => this._content(), {
      forbiddenTemplate: html`<campus-page-no-permission></campus-page-no-permission>`,
      context: ANY_CONTEXT,
    });
  }

  private _content() {
    return html`
      <cr-flex padding="${this.size.mobile ? 'md' : 'lg'}" space="lg">
        <cr-flex space="lg">
          <cr-h2> Lerninhalte </cr-h2>
          <cr-text>
            Willkommen, ${this._currentUser.value?.vorname}!👋 Von hier aus kannst du Lerninhalte für deine
            Schüler:innen erstellen und verwalten.
          </cr-text>
        </cr-flex>
        <cr-flex
          ${css({
            position: 'sticky',
            top: '0',
            zIndex: '1',
            background: this.colors.neutral.shade(50),
            width: '100%',
          })}
        >
          <cr-flex
            horizontal
            crossaxis-center
            space-between
            space="md"
            ${css({
              background: this.colors.neutral.shade(50),
              paddingTop: this.spacing.md,
              paddingBottom: this.spacing.sm,
              flexWrap: 'wrap',
              alignItems: 'end',
              width: '100%',
            })}
          >
            <cr-card horizontal space="md" wrap ${css({ flexGrow: '1' })} crossaxis-center>
              ${this._getAdvancedFilter()} ${this._getSubjectFilter()} ${this._getGradeFilter()}
              <cr-input
                ${css({ minWidth: '100px' })}
                @value-changed=${(e: ValueEvent<string>) => {
                  if (this._userSearchText.value === e.detail.value) return;
                  this._userSearchText.next(e.detail.value);
                }}
                placeholder="Suchen..."
                placeholder-mode="inline-floating"
                icon="search"
                flex
              ></cr-input>
              ${this._getControlButtons()}
            </cr-card>
          </cr-flex>
        </cr-flex>

        ${this._getSectionLessonList()}
      </cr-flex>
    `;
  }

  private _getAdvancedFilter() {
    return html`
      <cr-dropdown-selection
        ${css({ minWidth: '100px' })}
        flex
        placeholder=${'Versteckt?'}
        clearable
        .selectables=${[true, false]}
        .renderString=${(x: Maybe<boolean>) => (x ? 'Ja' : 'Nein')}
        @value-picked=${(e: ValueEvent<Maybe<boolean>>) => {
          this._userHiddenFromStudents.next(e.detail.value);
          this.requestUpdate();
        }}
      >
      </cr-dropdown-selection>
    `;
  }

  private _getSubjectFilter() {
    return html`<cr-dropdown-multiselection
      ${css({ minWidth: '100px' })}
      flex
      placeholder="Fächer"
      .selectables=${availableCampusSubjects}
      .value=${this._userCheckedSubjects.value}
      @value-picked=${(e: ValueEvent<string[]>) => {
        this._userCheckedSubjects.next(e.detail.value);
        this._updateList = this._updateList + 1;
      }}
    >
    </cr-dropdown-multiselection>`;
  }

  private _getGradeFilter() {
    return html`
      <cr-dropdown-multiselection
        ${css({ minWidth: '100px' })}
        flex
        placeholder="Jahrgangsstufe"
        .selectables=${ChancenwerkUser.availableGrades}
        .value=${this._userCheckedGrades.value}
        @value-picked=${(e: ValueEvent<number[]>) => {
          this._userCheckedGrades.next(e.detail.value);
          this._updateList = this._updateList + 1;
        }}
      >
      </cr-dropdown-multiselection>
    `;
  }

  private _getSectionLessonList() {
    return html`
      <cr-flex space="md">
        ${guard(
          [
            this._userCheckedGrades.value,
            this._userCheckedSubjects.value,
            this._userSearchText.value,
            this._updateList,
          ],
          () => html`
            <campus-component-section-lesson-list
              .mode=${'edit'}
              .userCheckedGrades=${this._userCheckedGrades.value}
              .userCheckedSubjects=${this._userCheckedSubjects.value}
              .userSearchText=${this._userSearchText.value}
              .userHiddenFromStudents=${this._userHiddenFromStudents}
              .area=${CampusArea.STUDENTS}
            ></campus-component-section-lesson-list>
          `,
        )}
      </cr-flex>
    `;
  }

  private _getControlButtons() {
    return html`
      <chancen-component-context-menu
        mode="action"
        icon="add"
        .items=${[
          {
            icon: 'add',
            label: 'Lektion',
            action: async () => {
              window.open(CampusRoutes.lessons + '/edit?area=' + CampusArea.STUDENTS);
            },
          },
          {
            icon: 'add',
            label: 'Themenbereich',
            action: async () => {
              await CampusDialogEditSection.showPopup({
                draggable: this.size.mobile || this.size.tablet,
                modal: true,
                props: {
                  area: CampusArea.STUDENTS,
                },
              });
            },
          },
        ]}
      >
        Hinzufügen
      </chancen-component-context-menu>
    `;
  }
}
